import React, { useEffect, useRef , useMemo, useState} from 'react';
import './ToolSettings.css';
import { transformServerDataForApp, transformData2UI } from './config';


const sendAppCommand = (message) => {
  // iOS
  if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.reactNative) {
    window.webkit.messageHandlers.reactNative.postMessage(message);
  }
  // Android
  if (window.android) {
    window.android.postMessage(message);
  }
};

// Card组件用于渲染单个（左/右）耳的设置
const NoiseReductionCard = ({
    strengthValue,
    strengthLabel,
    strFuction,
    color,
    label,
}) => {
    // 决定卡片的类名，例如 'card-left' 或 'card-right'
    const cardClass = `noise-card card-${color}`;

    return (
        <div className={cardClass}>
            {/* 左右耳标签按钮 */}
            <div className={`ear-label ${color}`}>{label}</div>
            
            {/* 强度描述，如 Stronger */}
            <div className="strength-label">{strengthLabel}</div>
            
            {/* 降噪数值和描述 - 移动端会垂直堆叠 */}
            {strengthValue.length < 7 && (
                <div className="noise-reduction-text">
                    {strFuction}
                    <div className="strength-value">{strengthValue}</div>
                </div>
                )}
        </div>
        
    );
};

const sentCommandIds = new Set();
// 主组件
const ToolSettings = ({
    strategy = 1,
    strengthValue = "",
    leftOrRight = "A",
    isHistory = false, // 新增：如果是查看历史，可能不需要自动发指令
    isLast = false,
    msgId
}) => {
    const commandSentRef = useRef(false);
    const [enableBt1, setEnableBt1] = useState(false);
    const [enableBt2, setEnableBt2] = useState(false);
    // 1. 使用 useMemo 纯计算 UI 展示数据，避免每次渲染都重复转换
    const { leftEarData, rightEarData, title, subtitle, commandSend } = useMemo(() => {
        
        if (strategy === undefined || strategy === '' ||strategy === null) {
            return { leftEarData: null, rightEarData: null };
        }
  
        const data = { type: strategy, channel: leftOrRight, value: strengthValue };
        console.log("ToolSettings data:", data);
        const cmd = transformServerDataForApp(data);
        const uiData = transformData2UI(data);
        console.log("uiData:", uiData);
        return {
            leftEarData: uiData,
            rightEarData: uiData, // 这里根据业务逻辑逻辑调整
            title: uiData.title,
            subtitle: uiData.subTitle,
            commandSend: cmd
        };
    }, [strategy, leftOrRight, strengthValue]);

    // 2. 在 useEffect 中处理“只发一次”的逻辑
    useEffect(() => {
       // 如果是历史记录，直接拦截
        if (isHistory) return;

        // 2. 核心拦截逻辑：
        // 只有当 isLast 为 true，且该 msgId 从未发送过指令时才执行
        if (isLast && commandSend && !sentCommandIds.has(msgId) && msgId !== undefined && !commandSentRef.current ) {
            
            console.log('--- 真正执行指令发送 ---', msgId);
            
            // 3. 双重保险：同时更新局部 Ref 和 全局 Set
            sentCommandIds.add(msgId);
            commandSentRef.current = true;
            
            sendAppCommand(commandSend);
        }

    }, [commandSend, isHistory, msgId]); // 依赖 commandSend，确保内容变化且未发送时触发

    // 3. 渲染防护
    if (!leftEarData && !rightEarData) {
        return null;
    }

    const wrapperClass = `cards-wrapper ${
        leftEarData.channel === 3 ? 'dual-mode' : `single-mode ${leftEarData.channel === 1 ? 'left-ear' : 'right-ear'}`
    }`;

    const divLineClass = `${
        leftEarData.step.length < 7 ? 'divider-line' : 'divider-line-short'}`;

    const handleButtonApplyClick = () => {
        setEnableBt2(true)
        console.log("Apply Again button clicked:", commandSend);
        sendAppCommand(commandSend);
    };
    const handleButtonUndoClick = () => {
        setEnableBt1(true)
        let strategyTemp = strategy;
        if(strategy % 2 === 1){
            strategyTemp +=  1;
        }else{
            strategyTemp -=  1;
        }
        if(strategy < 17){
            const data = { type: strategyTemp, channel: leftOrRight };
            const cmdTemp = transformServerDataForApp(data);
            console.log("Undo button clicked:", cmdTemp);
            sendAppCommand(cmdTemp);
        }else{
            const data = { type: strategy, channel: leftOrRight, value: strengthValue };
            console.log("Undo button clicked: data ", data);
            const cmdTemp = transformServerDataForApp(data);
            const cmdTempJson = JSON.parse(cmdTemp);
            console.log("Undo button clicked:", "undo:" + cmdTempJson.type);
            sendAppCommand("undo:" + cmdTempJson.type);
        }
        
    };
    return (
        <div className="settings-container">
            <div className="header">
                <h2 className="title">{title}</h2>
                <div className="subtitle">{subtitle}</div>
            </div>

            <div className={`${wrapperClass}`}>
                {(leftEarData.channel ===1 || leftEarData.channel ===3) && (
                    <NoiseReductionCard
                        strengthValue={leftEarData.step}
                        strengthLabel={leftEarData.stronger}
                        strFuction={leftEarData.function}   
                        color="left"
                        label={leftEarData.label || "Left"}
                    />
                )}

                {/* 分割线：仅当左右耳数据都需要显示时才渲染 */}
                {(leftEarData.channel === 1 || leftEarData.channel === 3) && 
                (rightEarData.channel === 2 || rightEarData.channel === 3) && (
                    <div className={`${divLineClass}`} />
                )}

                {(rightEarData.channel ===2 || rightEarData.channel ===3) && (
                    <NoiseReductionCard
                        strengthValue={rightEarData.step}
                        strengthLabel={rightEarData.stronger}
                        strFuction={rightEarData.function}   
                        color="right"
                        label={rightEarData.label || "Right"}
                    />
                )}
                
            </div>
             {leftEarData.step.length > 7 && (
                <div className="only-step">{leftEarData.step}</div>
            )}

            {!isHistory && isLast && strategy !== 0 && (
                <div className="action-buttons">
                    <button className="action-button undo-button"
                        onClick={handleButtonUndoClick} disabled={enableBt1}>
                        {"Undo Change"}
                    </button>
                    <button 
                        className="action-button apply-button" 
                        onClick={handleButtonApplyClick} 
                         disabled={enableBt2}
                    >
                        {"Apply Again"}
                    </button>
                </div>
            )}
        </div>
    );
};

export default ToolSettings;